import React from "react";
import { styled } from "styled-components";
import { ConfigSchema } from "./configTypes";
import { severityStyle } from "./severity";
import { tokens } from "./tokens";

/**
 * The one reconciled operational status, as the API resolved it.
 *
 * Label, severity and help text all come from `operational_states` in the
 * schema. Nothing about the precedence between OFFLINE / SERVICING / GROUNDED /
 * AUTONOMY is re-derived here — that reconciliation is the whole job of
 * `operational_state.py`, and a second copy in TypeScript is exactly how the
 * grid, the table and the counters would start disagreeing.
 */
interface StateBadgeProps {
  state: string | null;
  schema: ConfigSchema | null;
  size?: "sm" | "md";
}

const StateBadge: React.FC<StateBadgeProps> = ({
  state,
  schema,
  size = "md",
}) => {
  if (!state) return null;
  // `operational_states` is absent for a non-AeroVect tenant. Today such a
  // caller also gets no `operational_state` on the row, so this can't be
  // reached — but the two facts live in different handlers, and a badge that
  // throws because a value arrived without its vocabulary is not a failure
  // worth having. Falls through to rendering the raw value.
  const def = schema?.operational_states?.find((s) => s.value === state);
  const style = severityStyle(def?.severity);
  return (
    <Pill
      $size={size}
      title={def?.help}
      style={{
        background: style.bg,
        color: style.color,
        borderColor: style.border,
      }}
    >
      {def?.label ?? state}
    </Pill>
  );
};

/** The pill both derived-status badges wear, so they read as one family. */
export const Pill = styled.span<{ $size: "sm" | "md" }>`
  display: inline-flex;
  align-items: center;
  border: 1px solid;
  border-radius: 999px;
  white-space: nowrap;
  font-weight: 600;
  letter-spacing: 0.01em;
  font-size: ${(p) => (p.$size === "sm" ? "11px" : "12px")};
  padding: ${(p) => (p.$size === "sm" ? "2px 8px" : "3px 11px")};
`;

/** Neutral counterpart for a vehicle with no state yet (snapshot mode). */
export const UnknownStateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px dashed ${tokens.line};
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: ${tokens.slate};
`;

export default StateBadge;
